'use client';

import React, { ReactNode } from 'react';
import classnames from 'classnames';
// import Skeleton from "../Skeleton/Skeleton"; // TODO: Convert Skeleton to TypeScript
import styles from './Typography.module.scss';

// Temporary Skeleton placeholder until conversion
const Skeleton: React.FC<{
  className?: string;
  theme?: string;
  width?: unknown;
  height?: string;
  shouldScale?: boolean;
}> = ({ className, theme }) => (
  <div
    className={className}
    // eslint-disable-next-line react/forbid-dom-props
    style={{
      backgroundColor: theme === 'dark' ? '#333' : '#e0e0e0',
      height: '1em',
      borderRadius: '4px',
    }}
  />
);

type TypographyVariant =
  | 'display'
  | 'headline'
  | 'subtitle'
  | 'body'
  | 'link'
  | 'superscript'
  | 'eyebrow'
  | 'label'
  | 'button'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6';

type TypographySize = 'extraSmall' | 'small' | 'medium' | 'large';
type TypographyStyle = 'default' | 'italic';
type TypographyAlign = 'left' | 'center' | 'right';
type Direction = 'ltr' | 'rtl';
type Theme = 'dark' | 'light';

type TypographyColor =
  | 'primary-1'
  | 'primary-2'
  | 'primary-3'
  | 'product-sapphire-1'
  | 'product-sapphire-2'
  | 'product-sapphire-3'
  | 'product-sapphire-4'
  | 'white'
  | 'gray-1'
  | 'gray-2'
  | 'gray-3'
  | 'gray-4'
  | 'gray-5'
  | 'gray-6'
  | 'gray-7'
  | 'gray-8'
  | 'gray-9'
  | 'black'
  | 'blue'
  | 'success-1'
  | 'warning-1'
  | 'error-1'
  | 'information-1';

type HTMLTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'label';

type SkeletonWidth = {
  xs: number;
  md: number;
  xl: number;
};

const TypographyVariants: Record<TypographyVariant, HTMLTag> = {
  display: 'h1',
  headline: 'h2',
  subtitle: 'h3',
  body: 'p',
  link: 'span',
  superscript: 'span',
  eyebrow: 'span',
  label: 'span',
  button: 'span',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  /** Typography variant type */
  variant?: TypographyVariant;
  /** Typography as type */
  as?: HTMLTag;
  /** Font size */
  size?: TypographySize;
  /** Font style */
  fontStyle?: TypographyStyle;
  /** Text align */
  align?: TypographyAlign;
  /** Text direction */
  direction?: Direction;
  /** Custom class name */
  className?: string;
  /** Text color */
  color?: TypographyColor;
  /** Children content */
  children?: ReactNode;
  /** Theme */
  theme?: Theme;
  /** Show skeleton loading */
  showSkeleton?: boolean;
  /** Skeleton width configuration */
  skeletonWidth?: SkeletonWidth;
}

const Typography: React.FC<TypographyProps> = ({
  variant = 'body',
  size = 'medium',
  fontStyle = 'default',
  color,
  children,
  align = 'left',
  direction = 'ltr',
  className,
  theme = 'dark',
  showSkeleton = false,
  skeletonWidth = { xs: 40, md: 60, xl: 80 },
  as = '',
  ...props
}) => {
  const HTMLTag = as ? as : TypographyVariants[variant];

  if (showSkeleton) {
    return (
      <Skeleton
        className={classnames(styles[variant], styles[size], styles[`direction-${direction}`])}
        width={skeletonWidth}
        height="auto"
        theme={theme}
        shouldScale={true}
      />
    );
  }

  const constructTag = () => {
    // Adjust color based on theme and current color.
    // If the theme is "light" and the color is "primary-1", use "gray-5" instead to ensure better visibility.
    const adjustedColor = theme === 'light' && color === 'primary-1' ? 'gray-5' : color;

    const classList = classnames(
      styles.resetStyles,
      styles[variant],
      styles[size],
      styles[fontStyle],
      adjustedColor && styles[`${theme}-${adjustedColor}`],
      styles[`align-${align}`],
      styles[`direction-${direction}`],
      props.onClick && styles.clickable,
      className
    );

    if (typeof children === 'string') {
      return React.createElement(HTMLTag, {
        className: classList,
        dangerouslySetInnerHTML: { __html: children },
        ...props,
      });
    }

    return React.createElement(
      HTMLTag,
      {
        className: classList,
        ...props,
      },
      children
    );
  };

  return constructTag();
};

Typography.displayName = 'Typography';

export default Typography;
export type {
  TypographyColor,
  TypographyVariant,
  TypographySize,
  TypographyAlign,
  TypographyStyle,
  Direction,
  Theme,
};
